import React, { useEffect, useState } from "react";
import { usePhrase } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiError, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, Smartphone, ShieldCheck, User, KeyRound, Languages, MonitorSmartphone,
  Trash2, CheckCircle2,
} from "lucide-react";

function guessDeviceLabel(ua) {
  if (!ua) return "Unknown device";
  const u = ua.toLowerCase();
  const browser =
    u.includes("edg/") ? "Edge" :
    u.includes("chrome/") ? "Chrome" :
    u.includes("firefox/") ? "Firefox" :
    u.includes("safari/") ? "Safari" :
    "Browser";
  const os =
    u.includes("android") ? "Android" :
    u.includes("iphone") || u.includes("ipad") ? "iOS" :
    u.includes("mac os x") || u.includes("macintosh") ? "Mac" :
    u.includes("windows") ? "Windows" :
    u.includes("linux") ? "Linux" :
    "Device";
  return `${browser} · ${os}`;
}

export default function Settings() {
  const pt = usePhrase();
  const { user, setUser } = useAuth();
  const { lang, setLang, t } = useLang();

  // --- Sign out of all devices ---
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const handleLogoutAll = async () => {
    setLoadingAll(true);
    try {
      await api.post("/auth/logout-all");
      toast.success(pt("Signed out of every device"));
      window.location.assign("/login");
    } catch (e) {
      toast.error(formatApiError(e));
      setLoadingAll(false);
    }
  };

  // --- Active sessions ---
  const [sessions, setSessions] = useState([]);
  const [sessLoading, setSessLoading] = useState(true);
  const loadSessions = async () => {
    setSessLoading(true);
    try {
      const { data } = await api.get("/settings/sessions");
      setSessions(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSessLoading(false); }
  };
  useEffect(() => { loadSessions(); }, []);
  const revokeOne = async (sid) => {
    try {
      await api.delete(`/settings/sessions/${sid}`);
      toast.success(pt("Session revoked"));
      loadSessions();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // --- Change password ---
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const submitPw = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pw.next.length < 8) { setPwError(pt("New password must be at least 8 characters.")); return; }
    if (pw.next !== pw.confirm) { setPwError(pt("New passwords do not match.")); return; }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success(pt("Password changed. Other devices have been signed out."));
      setPw({ current: "", next: "", confirm: "" });
      // Reload sessions since server dropped all other sessions
      loadSessions();
    } catch (e) {
      setPwError(formatApiError(e));
    } finally { setPwLoading(false); }
  };

  // --- Language preference ---
  const [langSaving, setLangSaving] = useState(false);
  const saveLanguage = async (next) => {
    if (next === (user?.language || lang)) return;
    setLangSaving(true);
    try {
      await api.post("/settings/preferences", { language: next });
      setLang(next);
      // Reflect the new preference in local user state so it survives navigation.
      if (user) setUser({ ...user, language: next });
      toast.success(next === "hi" ? pt("भाषा बदल दी गई") : pt("Language updated"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLangSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8" data-testid="settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{pt("Account")}</div>
        <h1 className="mt-2 brand-heading text-3xl sm:text-4xl text-[color:var(--bk-navy)]">{pt("Settings")}</h1>
        <p className="mt-2 text-sm text-[color:var(--bk-navy)]/70">
          {pt("Manage your session, security and personal preferences.")}
        </p>
      </div>

      {/* Profile summary */}
      <section className="rounded-2xl border border-[color:var(--bk-line)] bg-white p-6" data-testid="settings-profile-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[color:var(--bk-navy)] text-white grid place-items-center">
            <User className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-[color:var(--bk-navy)]">{user?.name || "Account"}</div>
            <div className="text-xs text-[color:var(--bk-navy)]/60">{user?.email}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="rounded-lg bg-[color:var(--bk-ivory-2)] px-3 py-2">
            <div className="uppercase tracking-widest text-[10px] text-[color:var(--bk-navy)]/55">{pt("Role")}</div>
            <div className="mt-0.5 font-semibold text-[color:var(--bk-navy)] capitalize">{user?.role ? t(`role_${user.role}`) : ""}</div>
          </div>
          <div className="rounded-lg bg-[color:var(--bk-ivory-2)] px-3 py-2">
            <div className="uppercase tracking-widest text-[10px] text-[color:var(--bk-navy)]/55">{pt("Language")}</div>
            <div className="mt-0.5 font-semibold text-[color:var(--bk-navy)]">{lang === "hi" ? "हिन्दी" : "English"}</div>
          </div>
          <div className="rounded-lg bg-[color:var(--bk-ivory-2)] px-3 py-2">
            <div className="uppercase tracking-widest text-[10px] text-[color:var(--bk-navy)]/55">{pt("Status")}</div>
            <div className="mt-0.5 font-semibold text-emerald-700">{pt("Active")}</div>
          </div>
        </div>
      </section>

      {/* Language preference */}
      <section className="rounded-2xl border border-[color:var(--bk-line)] bg-white p-6" data-testid="settings-language-card">
        <div className="flex items-center gap-2 mb-1">
          <Languages className="w-4 h-4 text-[color:var(--bk-navy)]" />
          <h2 className="brand-heading text-xl text-[color:var(--bk-navy)]">{pt("Language")}</h2>
        </div>
        <p className="text-sm text-[color:var(--bk-navy)]/70 mb-4">
          {pt("Pick the language you'd like across every device you sign in from.")}
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { code: "en", label: "English" },
            { code: "hi", label: "हिन्दी" },
          ].map((opt) => {
            const active = (user?.language || lang) === opt.code;
            return (
              <button
                key={opt.code}
                onClick={() => saveLanguage(opt.code)}
                disabled={langSaving}
                data-testid={`lang-choice-${opt.code}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors disabled:opacity-60 ${active ? "bg-[color:var(--bk-navy)] text-white border-[color:var(--bk-navy)]" : "bg-white text-[color:var(--bk-navy)] border-[color:var(--bk-line)] hover:border-[color:var(--bk-navy)]/40"}`}
              >
                {active && <CheckCircle2 className="w-3.5 h-3.5" />} {opt.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Change password */}
      <section className="rounded-2xl border border-[color:var(--bk-line)] bg-white p-6" data-testid="settings-password-card">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-[color:var(--bk-navy)]" />
          <h2 className="brand-heading text-xl text-[color:var(--bk-navy)]">{pt("Change password")}</h2>
        </div>
        <p className="text-sm text-[color:var(--bk-navy)]/70 mb-5">
          {pt("For your safety, every other signed-in device will be logged out after you change your password.")}
        </p>
        <form onSubmit={submitPw} className="space-y-4 max-w-md">
          <div>
            <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("Current password")}</label>
            <input
              type="password" required value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              data-testid="pw-current"
              className="mt-2 w-full rounded-lg bg-white border border-[color:var(--bk-line)] px-3 py-2.5 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("New password")}</label>
            <input
              type="password" required minLength={8} value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              data-testid="pw-new"
              className="mt-2 w-full rounded-lg bg-white border border-[color:var(--bk-line)] px-3 py-2.5 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none"
            />
            <div className="text-[11px] text-[color:var(--bk-navy)]/55 mt-1">{pt("Minimum 8 characters.")}</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[color:var(--bk-navy)]/70 uppercase tracking-widest">{pt("Confirm new password")}</label>
            <input
              type="password" required value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              data-testid="pw-confirm"
              className="mt-2 w-full rounded-lg bg-white border border-[color:var(--bk-line)] px-3 py-2.5 focus:border-[color:var(--bk-navy)] focus:ring-2 focus:ring-[color:var(--bk-navy)]/10 outline-none"
            />
          </div>
          {pwError && <div data-testid="pw-error" className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{pwError}</div>}
          <button
            type="submit" disabled={pwLoading}
            data-testid="pw-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--bk-navy)] hover:bg-[color:var(--bk-navy)]/90 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
          >
            <KeyRound className="w-3.5 h-3.5" /> {pwLoading ? pt("Saving…") : pt("Change password")}
          </button>
        </form>
      </section>

      {/* Active devices */}
      <section className="rounded-2xl border border-[color:var(--bk-line)] bg-white p-6" data-testid="settings-sessions-card">
        <div className="flex items-center gap-2 mb-1">
          <MonitorSmartphone className="w-4 h-4 text-[color:var(--bk-navy)]" />
          <h2 className="brand-heading text-xl text-[color:var(--bk-navy)]">{pt("Active devices")}</h2>
        </div>
        <p className="text-sm text-[color:var(--bk-navy)]/70 mb-4">
          {pt("Places where your account is currently signed in. Don't recognize one? Revoke it.")}
        </p>
        {sessLoading ? (
          <div className="text-sm text-[color:var(--bk-navy)]/55">{pt("Loading sessions…")}</div>
        ) : sessions.length === 0 ? (
          <div className="text-sm text-[color:var(--bk-navy)]/55">{pt("No active sessions.")}</div>
        ) : (
          <ul className="divide-y divide-[color:var(--bk-line)] rounded-xl border border-[color:var(--bk-line)]">
            {sessions.map((s) => (
              <li key={s.sid} className="p-4 flex items-start sm:items-center gap-4 flex-col sm:flex-row" data-testid={`session-${s.sid.slice(0,8)}`}>
                <div className="w-10 h-10 rounded-full bg-[color:var(--bk-ivory-2)] text-[color:var(--bk-navy)] grid place-items-center shrink-0">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[color:var(--bk-navy)]">{guessDeviceLabel(s.user_agent).replace("Browser", pt("Browser")).replace("Device", pt("Device"))}</div>
                    {s.is_current && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                        <CheckCircle2 className="w-3 h-3" /> {pt("This device")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--bk-navy)]/60 mt-0.5 truncate">IP {s.ip} · {pt("Signed in")} {formatDate(s.created_at)} · {pt("Last active")} {formatDate(s.last_seen_at)}</div>
                </div>
                {!s.is_current && (
                  <button
                    onClick={() => revokeOne(s.sid)}
                    data-testid={`session-revoke-${s.sid.slice(0,8)}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 text-xs font-semibold"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> {pt("Revoke")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Sign out of all devices */}
      <section className="rounded-2xl border border-[color:var(--bk-line)] bg-white p-6" data-testid="settings-security-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4 text-[color:var(--bk-emerald)]" />
          <h2 className="brand-heading text-xl text-[color:var(--bk-navy)]">{pt("Nuclear option")}</h2>
        </div>
        <p className="text-sm text-[color:var(--bk-navy)]/70 mb-5">
          {pt("Something feel off? Sign out of every device where your account is currently active — including this one.")}
        </p>

        <div className="rounded-xl border border-[color:var(--bk-line)] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-700 grid place-items-center shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-[color:var(--bk-navy)]">{pt("Sign out of all devices")}</div>
            <div className="text-sm text-[color:var(--bk-navy)]/65 mt-0.5">
              {pt("Instantly revokes every active session. You'll need to sign in again on each device.")}
            </div>
          </div>
          {!confirmAllOpen ? (
            <button
              onClick={() => setConfirmAllOpen(true)}
              data-testid="logout-all-btn"
              className="inline-flex items-center gap-2 rounded-full bg-amber-900 hover:bg-amber-800 text-white px-4 py-2.5 text-xs font-semibold whitespace-nowrap"
            >
              <LogOut className="w-3.5 h-3.5" /> {pt("Sign out everywhere")}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setConfirmAllOpen(false)}
                data-testid="logout-all-cancel"
                className="rounded-full border border-[color:var(--bk-line)] text-[color:var(--bk-navy)] bg-white px-3 py-2 text-xs font-semibold"
              >
                {pt("Cancel")}
              </button>
              <button
                onClick={handleLogoutAll}
                disabled={loadingAll}
                data-testid="logout-all-confirm"
                className="inline-flex items-center gap-2 rounded-full bg-amber-900 hover:bg-amber-800 text-white px-4 py-2 text-xs font-semibold disabled:opacity-60"
              >
                <LogOut className="w-3.5 h-3.5" /> {loadingAll ? pt("Signing out…") : pt("Yes, sign out everywhere")}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
